/**
 * PROPRIETARY NOTICE
 * THE ONESCENT DIAL - Container Constraints Hook
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Provides consistent container constraints for panels, modals, and overlays.
 * Ensures content fits within viewport while respecting safe areas and sidebars.
 * 
 * FEATURES:
 * - Device-aware max dimensions
 * - Safe area inset handling
 * - Sidebar/navigation awareness
 * - Standard overflow patterns
 * - Flexible vs fixed height modes
 * 
 * USAGE:
 * const { maxHeight, maxWidth, padding, style } = useContainerConstraints({
 *   type: 'modal',
 *   reserveTop: 60,      // Space for header
 *   reserveBottom: 80,   // Space for footer
 * });
 */

import { useMemo } from 'react';
import { useResponsive, BREAKPOINTS } from './useResponsive';

// Container type presets
export const CONTAINER_TYPES = {
  MODAL: 'modal',
  PANEL: 'panel',
  DRAWER: 'drawer',
  OVERLAY: 'overlay',
  CARD: 'card',
  FULLSCREEN: 'fullscreen',
};

// Default reservations for common UI elements
const DEFAULT_RESERVATIONS = {
  // Top: ticker/header height varies by device
  top: {
    xs: 48,   // Smaller header on phones
    sm: 56,
    md: 60,
    lg: 64,
  },
  // Bottom: footer, CyKL button, Emergent badge
  bottom: {
    xs: 100,
    sm: 90,
    md: 80,
    lg: 70,
  },
  // Sidebar width
  sidebar: {
    xs: 0,    // No sidebar on small phones
    sm: 56,   // Compact sidebar
    md: 64,
    lg: 72,
  },
};

/**
 * Get container constraints based on device and type
 * 
 * @param {Object} config - Configuration options
 * @returns {Object} Constraint values and style object
 */
export const useContainerConstraints = (config = {}) => {
  const responsive = useResponsive();
  
  return useMemo(() => {
    const {
      type = CONTAINER_TYPES.MODAL,
      reserveTop = null,      // Override default top reservation
      reserveBottom = null,   // Override default bottom reservation
      reserveSidebar = null,  // Override sidebar reservation
      minHeight = 200,
      minWidth = 280,
      maxHeightPercent = 90,  // Max percentage of viewport height
      maxWidthPercent = 95,   // Max percentage of viewport width
      respectSafeAreas = true,
      centerContent = true,
    } = config;
    
    const {
      width: viewportWidth,
      height: viewportHeight,
      safeAreaInsets,
      breakpoint,
      isMobile,
      isPhone,
      isTablet,
      deviceType,
    } = responsive;
    
    // Get breakpoint-specific reservations
    const getReservation = (reservations) => {
      const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl'];
      const currentIndex = breakpointOrder.indexOf(breakpoint);
      
      // Find the applicable value (same or smaller breakpoint)
      for (let i = currentIndex; i >= 0; i--) {
        const bp = breakpointOrder[i];
        if (reservations[bp] !== undefined) {
          return reservations[bp];
        }
      }
      return reservations.xs || 0;
    };
    
    // Calculate reservations
    const topReserve = reserveTop ?? getReservation(DEFAULT_RESERVATIONS.top);
    const bottomReserve = reserveBottom ?? getReservation(DEFAULT_RESERVATIONS.bottom);
    const sidebarReserve = reserveSidebar ?? getReservation(DEFAULT_RESERVATIONS.sidebar);
    
    // Safe area additions
    const safeTop = respectSafeAreas ? safeAreaInsets.top : 0;
    const safeBottom = respectSafeAreas ? safeAreaInsets.bottom : 0;
    const safeLeft = respectSafeAreas ? safeAreaInsets.left : 0;
    const safeRight = respectSafeAreas ? safeAreaInsets.right : 0;
    
    // Calculate available space
    const totalVerticalReserve = topReserve + bottomReserve + safeTop + safeBottom;
    const totalHorizontalReserve = sidebarReserve + safeLeft + safeRight;
    
    // Calculate max dimensions
    let maxHeight, maxWidth;
    
    switch (type) {
      case CONTAINER_TYPES.FULLSCREEN:
        maxHeight = viewportHeight - safeTop - safeBottom;
        maxWidth = viewportWidth - safeLeft - safeRight;
        break;
        
      case CONTAINER_TYPES.DRAWER:
        maxHeight = viewportHeight - safeTop - safeBottom;
        maxWidth = Math.min(
          viewportWidth * 0.85,
          isPhone ? 320 : isTablet ? 400 : 480
        );
        break;
        
      case CONTAINER_TYPES.PANEL:
        maxHeight = viewportHeight - totalVerticalReserve;
        maxWidth = Math.min(
          viewportWidth - totalHorizontalReserve,
          isPhone ? 360 : isTablet ? 420 : 520
        );
        break;
        
      case CONTAINER_TYPES.OVERLAY:
        // Smaller overlay - tooltip/popover size
        maxHeight = Math.min(viewportHeight * 0.6, 400);
        maxWidth = Math.min(viewportWidth - 32, 320);
        break;
        
      case CONTAINER_TYPES.CARD:
        maxHeight = Math.min(viewportHeight * 0.7, 500);
        maxWidth = Math.min(viewportWidth - 24, isPhone ? 340 : 400);
        break;
        
      case CONTAINER_TYPES.MODAL:
      default:
        maxHeight = Math.min(
          viewportHeight - totalVerticalReserve,
          viewportHeight * (maxHeightPercent / 100)
        );
        maxWidth = Math.min(
          viewportWidth - totalHorizontalReserve - 32,
          viewportWidth * (maxWidthPercent / 100),
          isPhone ? 400 : isTablet ? 560 : 640
        );
        break;
    }
    
    // Apply minimums
    maxHeight = Math.max(maxHeight, minHeight);
    maxWidth = Math.max(maxWidth, minWidth);
    
    // Calculate padding based on device
    const containerPadding = isPhone ? 12 : isTablet ? 16 : 20;
    
    // Build style object
    const style = {
      maxHeight: `${Math.round(maxHeight)}px`,
      maxWidth: `${Math.round(maxWidth)}px`,
      width: type === CONTAINER_TYPES.MODAL ? '100%' : undefined,
    };
    
    // Add centering for modals
    if (centerContent && type === CONTAINER_TYPES.MODAL) {
      style.margin = 'auto';
    }
    
    // Calculate position offsets for fixed positioning
    const offsets = {
      top: topReserve + safeTop,
      bottom: bottomReserve + safeBottom,
      left: sidebarReserve + safeLeft,
      right: safeRight,
    };
    
    return {
      // Dimensions
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      
      // Available space
      availableHeight: maxHeight,
      availableWidth: maxWidth,
      
      // Reservations
      reservations: {
        top: topReserve + safeTop,
        bottom: bottomReserve + safeBottom,
        left: sidebarReserve + safeLeft,
        right: safeRight,
      },
      
      // Safe areas
      safeAreaInsets: {
        top: safeTop,
        bottom: safeBottom,
        left: safeLeft,
        right: safeRight,
      },
      
      // Positioning offsets
      offsets,
      
      // Spacing
      padding: containerPadding,
      
      // Device info for conditional rendering
      isMobile,
      isPhone,
      isTablet,
      deviceType,
      breakpoint,
      
      // Ready-to-use style object
      style,
      
      // CSS class suggestions
      overflowClass: 'overflow-y-auto overflow-x-hidden',
      scrollClass: 'scrollbar-hide',
    };
    
  }, [responsive, config]);
};

/**
 * Preset configurations for common container types
 */
export const CONTAINER_PRESETS = {
  // Main application modals (Settings, Profile Manager, etc.)
  appModal: {
    type: CONTAINER_TYPES.MODAL,
    maxHeightPercent: 85,
    maxWidthPercent: 90,
  },
  
  // Information panels (Orientation, Deep Dive)
  infoPanel: {
    type: CONTAINER_TYPES.PANEL,
    reserveTop: 60,
    reserveBottom: 70,
  },
  
  // Drawer panels (History Storage)
  drawerPanel: {
    type: CONTAINER_TYPES.DRAWER,
    reserveTop: 0,
    reserveBottom: 0,
  },
  
  // Small overlays (tooltips, glossary)
  tooltip: {
    type: CONTAINER_TYPES.OVERLAY,
    respectSafeAreas: false,
    centerContent: false,
  },
  
  // Tutorial card
  tutorialCard: {
    type: CONTAINER_TYPES.CARD,
    reserveTop: 70,
    reserveBottom: 80,
    maxHeightPercent: 80,
  },
  
  // Comparative Analysis Setup
  caSetup: {
    type: CONTAINER_TYPES.MODAL,
    reserveTop: 80,
    reserveBottom: 100,
    maxHeightPercent: 80,
  },
};

/**
 * CSS utility classes for containers
 */
export const CONTAINER_CLASSES = {
  // Base container with proper overflow
  base: 'relative flex flex-col overflow-hidden',
  
  // Scrollable content area
  scrollable: 'flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide',
  
  // Fixed header within container
  header: 'flex-shrink-0 sticky top-0 z-10 bg-inherit',
  
  // Fixed footer within container
  footer: 'flex-shrink-0 sticky bottom-0 z-10 bg-inherit',
  
  // Content padding responsive
  contentPadding: 'px-3 sm:px-4 md:px-5 py-3 sm:py-4',
  
  // Full height with min-height safety
  fullHeight: 'min-h-0 h-full',
};

export default useContainerConstraints;
